import React, { useState, useEffect } from 'react';
import { useHistory, Link } from 'react-router-dom';
import {
  accessLocalStorage,
  getAllIncomingConfigs,
  deleteIncomingConfig,
  getIncomingStatistics
} from '../../../services';
import { hasFeature } from '../../../services/permissions';
import { describeDialedDestination } from '../../../services/incomingDialedDestination';

import Loading from '../../Loading/LoadingModule';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faListOl,
  faEdit,
  faTrash,
  faEye,
  faPhone,
  faSync,
  faChartLine
} from '@fortawesome/free-solid-svg-icons';

import './IncomingCalls.css';
import '../../../pages/CallcenterV2/config-styles.css';

function IncomingCallsList() {
  const history = useHistory();

  const [loading, setLoading] = useState(true);
  const [configs, setConfigs] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [userTenant, setUserTenant] = useState('');
  const [userRole, setUserRole] = useState(null);

  const [filters, setFilters] = useState({
    search: '',
    destinationType: '',
    activeOnly: true,
    hasTrunk: null
  });

  const [pagination, setPagination] = useState({
    currentPage: 1,
    itemsPerPage: 10,
    totalItems: 0
  });

  const [deleteModal, setDeleteModal] = useState({
    show: false,
    configId: null,
    configMask: ''
  });

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (userTenant) {
      loadConfigs();
    }
  }, [userTenant, filters, pagination.currentPage]);

  const loadInitialData = async () => {
    try {
      const user = await accessLocalStorage.getUserLocalStorage();
      if (!user?.user?.idtenant) {
        history.push('/');
        return;
      }

      const role = user.user.role;
      setUserRole(role);

      const tenant = user.user.idtenant;
      setUserTenant(tenant);

      await loadStatistics(tenant);

    } catch (error) {
      console.error('[INCOMING-LIST] Erro ao carregar dados iniciais:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadConfigs = async () => {
    try {
      setLoading(true);

      const requestFilters = {
        tenant: userTenant,
        activeOnly: filters.activeOnly,
        destinationType: filters.destinationType || null,
        hasTrunk: filters.hasTrunk,
        limit: pagination.itemsPerPage,
        offset: (pagination.currentPage - 1) * pagination.itemsPerPage
      };

      const response = await getAllIncomingConfigs(requestFilters);

      if (response.success) {
        let filteredConfigs = response.data || [];

        if (filters.search) {
          const searchLower = filters.search.toLowerCase();
          filteredConfigs = filteredConfigs.filter(config =>
            config.mask?.toLowerCase().includes(searchLower) ||
            config.destination_main?.toLowerCase().includes(searchLower) ||
            config.trunk_name?.toLowerCase().includes(searchLower) ||
            config.description?.toLowerCase().includes(searchLower)
          );
        }

        setConfigs(filteredConfigs);

        if (response.pagination) {
          setPagination(prev => ({
            ...prev,
            totalItems: response.pagination.total
          }));
        }
      } else {
        setConfigs([]);
      }

    } catch (error) {
      console.error('[INCOMING-LIST] Erro ao carregar configurações:', error);
      setConfigs([]);
    } finally {
      setLoading(false);
    }
  };

  const loadStatistics = async (tenant) => {
    try {
      const response = await getIncomingStatistics(tenant);
      if (response.success) {
        setStatistics(response.data);
      }
    } catch (error) {
      console.error('[INCOMING-LIST] Erro ao carregar estatísticas:', error);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const handleRefresh = () => {
    loadConfigs();
    loadStatistics(userTenant);
  };

  const handleDelete = async () => {
    if (!deleteModal.configId) return;

    try {
      setLoading(true);

      const response = await deleteIncomingConfig(deleteModal.configId);

      if (response.success) {
        setDeleteModal({ show: false, configId: null, configMask: '' });
        loadConfigs();
        loadStatistics(userTenant);
      } else {
        alert(`Erro ao deletar configuração: ${response.error}`);
      }

    } catch (error) {
      console.error('[INCOMING-LIST] Erro ao deletar:', error);
      alert('Erro ao deletar configuração');
    } finally {
      setLoading(false);
    }
  };

  const openDeleteModal = (config) => {
    setDeleteModal({
      show: true,
      configId: config.id,
      configMask: config.mask
    });
  };

  const closeDeleteModal = () => {
    setDeleteModal({
      show: false,
      configId: null,
      configMask: ''
    });
  };

  const getDestinationTypeLabel = (type) => {
    const types = {
      endpoint: 'Ramal',
      queue: 'Fila',
      'callcenter-v2': 'Callcenter V2',
      external: 'Externo',
      disa: 'DISA/URA',
      whatsapp: 'WhatsApp',
      hangup: 'Desligar',
      stasis: 'App Stasis'
    };
    return types[type] || type;
  };

  const getDestinationBadgeClass = (type) => {
    const classes = {
      endpoint: 'ccv2-badge--success',
      queue: 'ccv2-badge--warn',
      'callcenter-v2': 'ccv2-badge--info',
      external: 'ccv2-badge--info',
      disa: 'ccv2-badge--danger',
      whatsapp: 'ccv2-badge--success',
      hangup: 'ccv2-badge--danger',
      stasis: 'ccv2-badge--info'
    };
    return classes[type] || '';
  };

  const totalPages = Math.ceil(pagination.totalItems / pagination.itemsPerPage);

  if (loading && configs.length === 0) {
    return <Loading />;
  }

  return (
    <div className="incoming-page">
      <div className="incoming-content">

        <div className="incoming-header">
          <div className="incoming-title-wrapper">
            <FontAwesomeIcon icon={faPhone} className="incoming-icon" />
            <div>
              <h1 className="incoming-title">Chamadas de Entrada</h1>
              <p className="incoming-subtitle">Gerencie e controle suas configurações de entrada</p>
            </div>
          </div>
          <div className="incoming-actions">
            {hasFeature('config.chamadas.entrada', 'create') && (
            <Link to="/config/chamadas-entrada/criar" className="ccv2-btn ccv2-btn-primary" style={{ textDecoration: 'none' }}>
              <FontAwesomeIcon icon={faPlus} /> Nova Configuração
            </Link>
            )}
            {hasFeature('config.chamadas.entrada', 'create') && (
            <Link to="/config/chamadas-entrada/criar-faixa" className="ccv2-btn ccv2-btn-secondary" style={{ textDecoration: 'none' }}>
              <FontAwesomeIcon icon={faListOl} /> Cadastrar Faixa
            </Link>
            )}
          </div>
        </div>

        {statistics && (
          <div className="ccv2-dashboard-kpis">
            <div className="ccv2-dashboard-kpi">
              <div className="ccv2-dashboard-kpi-value">{statistics.total_configs || 0}</div>
              <div className="ccv2-dashboard-kpi-label">Total de Configurações</div>
            </div>
            <div className="ccv2-dashboard-kpi">
              <div className="ccv2-dashboard-kpi-value ccv2-kpi-green">{statistics.active_configs || 0}</div>
              <div className="ccv2-dashboard-kpi-label">Configurações Ativas</div>
            </div>
            <div className="ccv2-dashboard-kpi">
              <div className="ccv2-dashboard-kpi-value">{statistics.configs_with_trunk || 0}</div>
              <div className="ccv2-dashboard-kpi-label">Com Tronco</div>
            </div>
          </div>
        )}

        <div className="ccv2-filters">
          <div className="ccv2-filter-group" style={{ flex: 2 }}>
            <label>Buscar</label>
            <input
              type="text"
              placeholder="Máscara, tronco, destino..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
            />
          </div>
          <div className="ccv2-filter-group">
            <label>Tipo de Destino</label>
            <select
              value={filters.destinationType}
              onChange={(e) => handleFilterChange('destinationType', e.target.value)}
            >
              <option value="">Todos os tipos</option>
              <option value="endpoint">Ramal</option>
              <option value="queue">Fila</option>
              <option value="external">Externo</option>
              <option value="disa">DISA/URA</option>
              <option value="whatsapp">WhatsApp</option>
              <option value="hangup">Desligar</option>
            </select>
          </div>
          <div className="ccv2-filter-group">
            <label>Status</label>
            <select
              value={filters.activeOnly ? 'active' : 'all'}
              onChange={(e) => handleFilterChange('activeOnly', e.target.value === 'active')}
            >
              <option value="active">Apenas Ativos</option>
              <option value="all">Todos</option>
            </select>
          </div>
          <div className="ccv2-filter-group">
            <label>Tronco</label>
            <select
              value={filters.hasTrunk === null ? 'all' : (filters.hasTrunk ? 'yes' : 'no')}
              onChange={(e) => handleFilterChange('hasTrunk', e.target.value === 'all' ? null : e.target.value === 'yes')}
            >
              <option value="all">Todos</option>
              <option value="yes">Com tronco</option>
              <option value="no">Sem tronco</option>
            </select>
          </div>
          <div className="ccv2-filter-actions">
            <button className="ccv2-btn ccv2-btn-sm ccv2-btn-secondary" onClick={handleRefresh} title="Atualizar lista">
              <FontAwesomeIcon icon={faSync} />
            </button>
          </div>
        </div>

        <div className="ccv2-config-card">
          <div className="ccv2-config-table-wrapper">
            <table className="ccv2-config-table">
              <thead>
                <tr>
                  <th>Máscara DID</th>
                  <th>Tronco</th>
                  <th>Destino Principal</th>
                  <th>Recursos</th>
                  <th>Status</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {configs.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="ccv2-empty-row">
                      Nenhuma configuração de chamada de entrada encontrada.
                      {' '}
                      <Link to="/config/chamadas-entrada/criar" style={{ color: '#4A7BA7', fontWeight: 600 }}>
                        Criar nova configuração
                      </Link>
                    </td>
                  </tr>
                ) : (
                  configs.map(config => (
                    <tr key={config.id}>
                      <td>
                        <span className="ccv2-cell-name">{config.mask}</span>
                        {config.description && (
                          <span className="ccv2-cell-secondary">{config.description}</span>
                        )}
                      </td>
                      <td>
                        {config.trunk_name ? (
                          <span className="ccv2-badge ccv2-badge--info">{config.trunk_name}</span>
                        ) : (
                          <span style={{ color: '#94a3b8', fontSize: '0.8125rem' }}>Nenhum</span>
                        )}
                      </td>
                      <td>
                        <span className={`ccv2-badge ${getDestinationBadgeClass(config.destination_type_main)}`}>
                          {getDestinationTypeLabel(config.destination_type_main)}
                        </span>
                        {config.destination_main && (
                          <span className="ccv2-cell-secondary">
                            {describeDialedDestination(config.destination_type_main, config.destination_main) || config.destination_main}
                          </span>
                        )}
                      </td>
                      <td>
                        <div className="ccv2-tags">
                          {parseInt(config.overflow_count) > 0 && (
                            <span className="ccv2-badge ccv2-badge--warn" title="Transbordos configurados">
                              {config.overflow_count}x OVF
                            </span>
                          )}
                          {parseInt(config.gotoiftime_count) > 0 && (
                            <span className="ccv2-badge ccv2-badge--info" title="Time-conditions configuradas">
                              {config.gotoiftime_count}x TC
                            </span>
                          )}
                          {config.recording_enabled && (
                            <span className="ccv2-badge ccv2-badge--danger" title="Gravação habilitada">REC</span>
                          )}
                          {config.audio_enabled && (
                            <span className="ccv2-badge ccv2-badge--success" title="Áudio habilitado">AUD</span>
                          )}
                          {!parseInt(config.overflow_count) && !parseInt(config.gotoiftime_count) && !config.recording_enabled && !config.audio_enabled && (
                            <span style={{ color: '#94a3b8', fontSize: '0.75rem' }}>-</span>
                          )}
                        </div>
                      </td>
                      <td>
                        {config.active ? (
                          <span className="ccv2-badge ccv2-badge--success">Ativo</span>
                        ) : (
                          <span className="ccv2-badge ccv2-badge--danger">Inativo</span>
                        )}
                      </td>
                      <td className="ccv2-cell-actions">
                        <Link
                          to={`/config/chamadas-entrada/${config.id}/visualizar`}
                          className="ccv2-btn-icon ccv2-btn-edit"
                          title="Visualizar"
                        >
                          <FontAwesomeIcon icon={faEye} />
                        </Link>
                        <Link
                          to={`/config/chamadas-entrada/${config.id}/editar`}
                          className="ccv2-btn-icon ccv2-btn-edit"
                          title="Editar"
                        >
                          <FontAwesomeIcon icon={faEdit} />
                        </Link>
                        {hasFeature('config.chamadas.entrada', 'delete') && (
                          <button
                            className="ccv2-btn-icon ccv2-btn-delete"
                            onClick={() => openDeleteModal(config)}
                            title="Excluir"
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="ccv2-pagination">
              <button
                className="ccv2-btn ccv2-btn-sm ccv2-btn-outline"
                onClick={() => setPagination(prev => ({ ...prev, currentPage: prev.currentPage - 1 }))}
                disabled={pagination.currentPage === 1}
              >
                Anterior
              </button>
              <span className="ccv2-page-number">
                Página {pagination.currentPage} de {totalPages}
              </span>
              <button
                className="ccv2-btn ccv2-btn-sm ccv2-btn-outline"
                onClick={() => setPagination(prev => ({ ...prev, currentPage: prev.currentPage + 1 }))}
                disabled={pagination.currentPage === totalPages}
              >
                Próxima
              </button>
            </div>
          )}
        </div>

        <div style={{ textAlign: 'center' }}>
          <Link
            to="/config/chamadas-entrada/estatisticas"
            className="ccv2-btn ccv2-btn-secondary"
            style={{ textDecoration: 'none' }}
          >
            <FontAwesomeIcon icon={faChartLine} /> Ver Estatísticas Detalhadas
          </Link>
        </div>

      </div>

      {deleteModal.show && (
        <div className="ccv2-modal-overlay" onClick={closeDeleteModal}>
          <div className="ccv2-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ccv2-modal-header" style={{ background: 'linear-gradient(135deg, #dc2626, #b91c1c)' }}>
              <h2>
                <FontAwesomeIcon icon={faTrash} /> Confirmar Exclusão
              </h2>
              <button className="ccv2-modal-close" onClick={closeDeleteModal}>
                &times;
              </button>
            </div>
            <div className="ccv2-modal-body">
              <p style={{ marginBottom: '1rem' }}>
                Tem certeza que deseja excluir a configuração para a máscara:
              </p>
              <p style={{ textAlign: 'center', margin: '1.5rem 0' }}>
                <strong style={{ fontSize: '1.25rem', color: '#dc2626' }}>{deleteModal.configMask}</strong>
              </p>
              <div className="incoming-warning-box">
                Esta ação não pode ser desfeita. A configuração será removida do banco de dados e do dialplan do Asterisk.
              </div>
            </div>
            <div className="ccv2-modal-footer">
              <button className="ccv2-btn ccv2-btn-secondary" onClick={closeDeleteModal}>
                Cancelar
              </button>
              <button
                className="ccv2-btn"
                style={{ background: '#dc2626', color: '#fff' }}
                onClick={handleDelete}
              >
                <FontAwesomeIcon icon={faTrash} /> Confirmar Exclusão
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default IncomingCallsList;
